import Vue from 'vue';
import { has, merge, pick, omitBy, isNil } from 'lodash';
import { saveStudentActivity } from '@/api/student.api';
import { requestRoot } from '@/axios';
import { findJWTByProfileId } from '@/helpers/auth';
import { AUTH_CHILD_REQUIRED_FIELDS } from '@/helpers/profile/requirements';

function useState() {
  return {
    activeChildId: null,
    children: [],
    createProfileData: getNewProfileInitialData(),
  };
}

const getters = {
  activeChildProfile(state, gettersArg) {
    return gettersArg.getChildren.find(c => c.id === state.activeChildId)
      || gettersArg.getChildren[0];
  },
  getChildren(state, gettersArg, rootState, rootGetters) {
    return getUniqueChildProfiles(state.children, rootGetters['parent/parentChildren']);
  },
  getChildrenBySearchStr: (_, gettersArg) => (search) => {
    const value = search.toLowerCase();

    return gettersArg.getChildren.filter((account) => {
      return (account.first_name && account.first_name.toLowerCase().includes(value))
            || (account.last_name && account.last_name.toLowerCase().includes(value))
            || (account.nickname && account.nickname.toLowerCase().includes(value));
    });
  },
  newChildProfile: state => state.createProfileData,
};

const actions = {
  loadChildProfile(context, { profileId } = {}) {
    const id = profileId || context.getters.activeChildProfile.id;
    if (!id) {
      throw new Error('No profile id specified to load child profile');
    }

    return requestRoot.get(`/accounts/child/${id}`)
      .then((res) => {
        const { profile } = res.data;
        context.dispatch('addChildren', [profile]);
        return profile;
      });
  },
  switchChildProfile(context, { profileId }) {
    const token = findJWTByProfileId(context.rootGetters, profileId);
    return requestRoot.get(`/accounts/child/${profileId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        const { profile } = res.data;
        context.dispatch('addChildren', [profile]);
        context.dispatch('setActiveChildId', profile.id);
        return profile;
      });
  },
  loginChild(context, { profileId, password, rememberMe }) {
    return requestRoot.post('/accounts/child/login', {
      profileId,
      password,
      rememberMe,
    })
      .then((res) => {
        const { profile } = res.data;
        context.dispatch('addChildren', [profile]);
        context.dispatch('setActiveChildId', profile.id);
        return profile;
      });
  },
  logoutChild({ dispatch }, profileId) {
    dispatch('removeChild', profileId);
  },
  createChild(context, payload) {
    const { grade, gender, avatar, nickname } = payload;

    const childAccountData = {
      grade,
      gender,
      avatar,
      nickname,
    };

    return requestRoot.post('/accounts/child', childAccountData)
      .then((res) => {
        const { profile } = res.data;

        context.dispatch('addNewChild', profile);
        context.dispatch('setActiveChildId', profile.id);
        context.dispatch('clearNewProfile');

        return profile;
      });
  },
  updateActiveChild(context, payload) {
    const activeChild = context.getters.activeChildProfile;
    const update = payload || omitBy(context.getters.newChildProfile, isNil);

    return requestRoot.put(`/accounts/child/edit/${activeChild.id}`, update)
      .then((res) => {
        const { profile } = res.data;

        context.dispatch('updateActiveChildState', profile);
        context.dispatch('clearNewProfile');

        return profile;
      });
  },
  updateActiveChildState(context, profile) {
    const activeChild = context.getters.activeChildProfile;
    const activeChildInState = context.state.children.find(c => c.id === activeChild.id);
    if (activeChildInState) {
      context.commit('UPDATE_ACTIVE_CHILD', profile);
    }
    const parentHasActiveChild = context.rootGetters['parent/parentChildren'].find(c => c.id === activeChild.id);
    if (parentHasActiveChild) {
      context.dispatch('parent/updateParentChild', { profileId: activeChild.id, update: profile }, { root: true });
    }
  },
  sendSchoolLinkingRequest({ dispatch }, { schoolProfileId }) {
    return requestRoot.post('/accounts/child/school/request-linking', { schoolProfileId })
      .then((res) => {
        const { requestedSchoolId } = res.data;
        dispatch('updateActiveChildState', { requested_school_id: requestedSchoolId });
        return res;
      });
  },
  cancelSchoolLinking({ dispatch }, { confirmationSchoolId }) {
    return requestRoot.post('/accounts/child/school/cancel-linking', { confirmationSchoolId })
      .then((res) => {
        const childUpdate = { requested_school_id: null, school_id: null };
        dispatch('updateActiveChildState', childUpdate);
        return res;
      });
  },
  sendUpdateEmailCodes(context, { newEmail }) {
    return requestRoot.post('/accounts/child/reset/email', { newEmail });
  },
  updateChildEmail(context, { emailCode, newEmail, newEmailCode }) {
    return requestRoot.put('/accounts/child/update/email', {
      emailCode,
      newEmailCode,
    })
      .then(() => context.dispatch('updateActiveChildState', { email: newEmail }));
  },
  sendResetPasswordCode(context, { profileId } = {}) {
    return requestRoot.post('/accounts/child/reset/password', { profileId });
  },
  updateChildPassword(context, { profileId, resetCode, password, newPassword }) {
    const data = {
      profileId,
      resetCode,
      password,
      newPassword,
    };
    return requestRoot.put('/accounts/child/update/password', data);
  },
  addChildren({ commit }, children) {
    commit('ADD_CHILDREN', children);
  },
  addNewChild({ commit }, child) {
    commit('ADD_CHILDREN', [child]);
    commit('UPDATE_NEW_PROFILE', getNewProfileInitialData());
  },
  setActiveChildId({ commit }, profileId) {
    commit('SET_ACTIVE_CHILD_ID', profileId);
  },
  removeChild({ commit }, profileId) {
    commit('REMOVE_CHILD', profileId);
  },
  updateNewProfile({ commit }, profileUpdate) {
    commit('UPDATE_NEW_PROFILE', profileUpdate);
  },
  clearNewProfile({ commit }) {
    commit('UPDATE_NEW_PROFILE', getNewProfileInitialData());
  },
  async saveActivity({ commit }, { child_id, time }) {
    const res = await saveStudentActivity(child_id, time);
    if (res.status === 200) {
      commit('UPDATE_LAST_PLAYED_DATE', { child_id, last_played: new Date().toISOString() });
    }
    return res;
  },
  runTimer({ commit }, child_id) {
    commit('RUN_TIMER', child_id);
  },
  resetTimer({ commit }, profile) {
    commit('RESET_TIMER', profile.id);
  },
};

const mutations = {
  ADD_CHILDREN(state, children = []) {
    state.children = getUniqueChildProfiles(state.children, children);
  },
  REMOVE_CHILD(state, profileId) {
    const childIdx = state.children.findIndex(c => c.id === profileId);
    if (childIdx !== -1) {
      state.children.splice(childIdx, 1);
    }
  },
  UPDATE_NEW_PROFILE(state, profileUpdate) {
    Object.assign(state.createProfileData, profileUpdate);
  },
  SET_ACTIVE_CHILD_ID(state, profileId) {
    state.activeChildId = profileId;
  },
  UPDATE_ACTIVE_CHILD(state, child) {
    if (!child) return;

    const idx = state.children.findIndex(c => c.id === state.activeChildId);
    if (idx !== -1) {
      const reactiveChild = createReactiveChild(child, state.children[idx]);
      Vue.set(state.children, idx, reactiveChild);
    }
  },
  RUN_TIMER(state, child_id) {
    const index = state.children.findIndex(child => child.id === child_id);
    state.children[index].played_time += 1000;
  },
  RESET_TIMER(state, child_id) {
    const index = state.children.findIndex(child => child.id === child_id);
    state.children[index].played_time = 0;
  },
  UPDATE_LAST_PLAYED_DATE(state, { child_id, last_played }) {
    const index = state.children.findIndex(child => child.id === child_id);
    state.children[index].last_played = last_played;
  },
};

function getNewProfileInitialData() {
  return {
    grade: null,
    gender: null,
    avatar: null,
    nickname: null,
  };
}

function getUniqueChildProfiles(initialList, newChildren) {
  const uniqChildren = [...initialList];

  newChildren.forEach((child) => {
    const existingChildIdx = uniqChildren.findIndex(c => c.id === child.id);
    if (existingChildIdx !== -1) {
      const updatedChild = isFilledDataChild(child)
        ? child
        : merge({}, uniqChildren[existingChildIdx], child);

      uniqChildren.splice(existingChildIdx, 1, updatedChild);
    } else {
      uniqChildren.push(child);
    }
  });

  return uniqChildren;
}

function createReactiveChild(child, oldChild = {}) {
  const reactiveChild = AUTH_CHILD_REQUIRED_FIELDS.some(f => has(child, f))
    ? pick(oldChild, AUTH_CHILD_REQUIRED_FIELDS)
    : oldChild;

  Object.entries(child).forEach(([key, value]) => {
    Vue.set(reactiveChild, key, value);
  });

  return reactiveChild;
}

function isFilledDataChild(child) {
  return AUTH_CHILD_REQUIRED_FIELDS.every(f => has(child, f));
}

export default {
  namespaced: true,
  state: useState,
  getters,
  actions,
  mutations,
};
